import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils';
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { nanoid } from 'nanoid';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTheme } from 'styled-components';

import { BottomRow } from './bottom-row';
import { TableState } from './types';

declare module '@tanstack/table-core' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // store the itemRank info
  addMeta({ itemRank });

  // return if the item should be filtered in/out
  return itemRank.passed;
};

type ColumnsFilter = {
  columnId: string;
  filterBy: string | string[] | number | number[];
  hide?: boolean;
};

interface TableProps<T extends object> {
  data: T[];
  columns: ColumnDef<T, any>[];
  hiddenColumnIds?: string[];
  fetchNextPage?: () => void;
  state?: TableState;
  filters?: ColumnsFilter[];
  onRowClick?: (data: T) => void;
  onRowSelect?: (data: T[]) => void;
  resetRowSelect?: number;
  estimatedRows?: number;
  bulkActions?: boolean;
}

export const Table = <T extends object>({
  data,
  columns,
  fetchNextPage,
  state = 'none',
  filters,
  onRowClick,
  onRowSelect,
  resetRowSelect,
  estimatedRows,
  hiddenColumnIds = [],
  bulkActions = true,
}: TableProps<T>) => {
  const theme: any = useTheme();

  // we need a reference to the scrolling element for logic down below
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data,
    columns,
    state: { rowSelection, sorting, columnFilters, globalFilter, columnVisibility },
    enableRowSelection: true,
    globalFilterFn: fuzzyFilter,
    filterFns: { fuzzy: fuzzyFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
  });

  // hide columns programmatically
  useEffect(() => {
    for (const column of table.getAllLeafColumns()) {
      if (hiddenColumnIds?.includes(column.id) && column.getIsVisible())
        column.toggleVisibility(false);
    }
  }, [table]);

  // when row selection triggers, invoke onRowSelect method
  useEffect(() => {
    const selectedRows = table
      .getSelectedRowModel()
      .flatRows.map(({ original }) => original);
    onRowSelect && onRowSelect(selectedRows);
  }, [rowSelection]);

  // use a counter from parent class to reset row selection
  useEffect(() => {
    setRowSelection({});
  }, [resetRowSelect]);

  const { rows } = table.getRowModel();

  // hook to determine whether the user is at bottom of the table. So we can call onFetchNextPage()
  const { ref, inView } = useInView();

  // check useEffect methods for more details: use to re trigger fetch()
  const [inViewReset, setInViewReset] = useState(nanoid());

  // virtualizing is optional, but might be necessary if we are going to potentially have hundreds or thousands of rows
  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: estimatedRows ? estimatedRows : rows.length,
    estimateSize: () => 50,
  });

  // pre-applied filters
  useEffect(() => {
    if (filters?.length) {
      for (const column of filters) {
        table.getColumn(column.columnId).setFilterValue(column.filterBy);
        column.hide && table.getColumn(column.columnId).toggleVisibility(false);
      }
    }
  }, []);

  // if user is within view port, load more data
  const onFetchNextPage = () => fetchNextPage && fetchNextPage();
  useEffect(() => {
    if (inView && state !== 'loading') onFetchNextPage();

    // we set 1 sec delay to re-run this method if the row is still within the view
    const interval = setInterval(() => {
      if (inView) setInViewReset(nanoid());
    }, 1500);

    // if there's no more data or loading, we clear up the interval
    if (state === 'no-data' || state === 'loading') clearInterval(interval);

    // clean up the interval on unmount
    return () => clearInterval(interval);
  }, [inView, inViewReset]);

  const onRowClickHandler = (row: Row<T>) => {
    // if the user has selected a row, then toggle the selection (click on rows)
    // so we can use row selection for bulk actions
    if (bulkActions && table.getIsSomeRowsSelected()) {
      row.toggleSelected();
      return;
    }

    if (!bulkActions) {
      // if bulk actions are not enabled, then we just toggle the row selection
      // reset previous selection.
      // this is useful when we have a single row selection
      setRowSelection({});
      row.toggleSelected();
    }

    onRowClick && onRowClick(row.original);
  };

  return (
    <div
      className="flex flex-col min-w-full gap-4"
      ref={tableContainerRef}
      id="step-measurement-table"
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <div className="flex px-10" key={headerGroup.id}>
          {headerGroup.headers.map((header) =>
            header.id === 'select' ? (
              <div className="mr-8 font-semibold py-7" key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            ) : (
              <div className="flex-1 font-semibold py-7" key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            ),
          )}
        </div>
      ))}
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index] as Row<T>;
        if (!row) return null;
        return (
          <div
            className={`flex border rounded-3xl px-10 transition-all ease-in-out duration-100 bg-gray ${
              onRowClick ? 'cursor-pointer' : ''
            } ${
              row.getIsSelected()
                ? `bg-${theme.current}-light`
                : 'hover:bg-black hover:bg-opacity-5'
            }`}
            key={row.id}
            onClick={() => onRowClickHandler(row)}
            role="presentation"
          >
            {row.getVisibleCells().map((cell) =>
              cell.id.includes('select') ? (
                <div className="flex items-center h-20 mr-8 text-gray-600" key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              ) : (
                <div
                  className="flex items-center flex-1 h-20 text-gray-600"
                  key={cell.id}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              ),
            )}
          </div>
        );
      })}
      <BottomRow state={state} ref={ref} fetchNextPage={onFetchNextPage} />
    </div>
  );
};
